import { useQuery } from '@tanstack/react-query';

interface Post {
  id: number;
  title: { rendered: string };
  date: string;
  excerpt: { rendered: string };
  _embedded: {
    'wp:featuredmedia': Array<{ source_url: string }>;
  };
  categories: number[];
  guid: { rendered: string };
}

// Known dead as of 2026-08-20: the blog migrated to Odoo, this WordPress API
// returns 404, and the response carries no CORS header, so the browser
// reports a network error before the status is readable. Kept pointing here
// until the replacement feed exists (quest M1); the UI now shows an honest
// error state instead of a blank when these fail.
const POSTS_URL = 'https://www.moyeecoffee.com/wp-json/wp/v2/posts?_embed&per_page=40';
const CATEGORIES_URL = 'https://www.moyeecoffee.com/wp-json/wp/v2/categories';

const fetchPosts = async (): Promise<Post[]> => {
  const response = await fetch(POSTS_URL);
  if (!response.ok)
    throw new Error(
      `News posts request failed: HTTP ${response.status} ${response.statusText} from ${POSTS_URL}`,
    );
  return response.json();
};

export const usePosts = () => {
  return useQuery(['posts'], fetchPosts, {
    // One retry, not React Query's default three: the upstream is known-dead
    // (AUDIT-FINDINGS.md addendum 2026-08-24), and three exponential-backoff
    // retries kept visitors on "Loading…" for ~10s before the honest state
    // appeared - which read as an intermittent failure. Founder decision
    // 2026-08-24 (quest D10, option B).
    retry: 1,
    onError: (error) => console.error(`[News] posts feed unavailable (${POSTS_URL}):`, error),
  });
};

const fetchCategories = async (): Promise<{ [key: number]: string }> => {
  const response = await fetch(CATEGORIES_URL);
  if (!response.ok)
    throw new Error(
      `News categories request failed: HTTP ${response.status} ${response.statusText} from ${CATEGORIES_URL}`,
    );

  const categoriesData = await response.json();
  const categoriesMap: { [key: number]: string } = {};

  categoriesData.forEach((category: { id: number; name: string }) => {
    categoriesMap[category.id] = category.name;
  });

  return categoriesMap;
};

export const useCategories = () => {
  return useQuery(['categories'], fetchCategories, {
    // Same retry rationale as usePosts above.
    retry: 1,
    onError: (error) =>
      console.error(`[News] categories feed unavailable (${CATEGORIES_URL}):`, error),
  });
};
